import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useParams, useLocation, useSearch } from "wouter";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { GradeTierBadge, type GradeTier } from "@/components/grade-tier-badge";
import { ProductCard } from "@/components/product-card";
import { ChevronRight, Filter, Home as HomeIcon, X, Boxes, Info, Search } from "lucide-react";

interface CategoryProduct {
  modelId: string;
  brand: string;
  modelName: string;
  description: string;
  imageUrl: string | null;
  startingPrice: number;
  grades: string[];
  gradePrices: Record<string, number>;
  msrp: string | null;
  color: string | null;
  attributes: any;
  isPallet: boolean;
  palletConstituentImages?: string[];
  palletConstituentModelCount?: number;
  palletTotalUnits?: number;
  palletConstituentBrands?: string[];
  palletConstituents?: Array<{
    brand: string;
    mfgSku: string;
    descriptor?: string | null;
    quantity: number;
    btu: string | null;
    msrpCents: number;
  }>;
  palletMsrpTotalCents?: number;
  palletGrade?: string | null;
  palletPerItemStartingPriceCents?: number | null;
  hasInStock: boolean;
  hasPresale: boolean;
  earliestShipDate: string | null;
  remoteWarehouseCodes: string[];
  minOrderQty: number;
  orderMultiple: number;
}

interface FacetEntry { name: string; count: number }
interface GradeFacet { code: string; tier: GradeTier; count: number }

interface CategoryResponse {
  category: {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    heroBlurb: string | null;
    showroomImage: string | null;
    gradeScaleHint: string;
    types: string[] | null;
  };
  products: CategoryProduct[];
  facets: {
    brands: FacetEntry[];
    grades: GradeFacet[];
    types: FacetEntry[];
  };
  totalCount: number;
  page: number;
  perPage: number;
  hasMore: boolean;
}

const SORT_OPTIONS = [
  { value: "price_low", label: "Price: Low → High" },
  { value: "price_high", label: "Price: High → Low" },
  { value: "name_asc", label: "Name A → Z" },
  { value: "discount", label: "Biggest Savings" },
];

const PAGE_SIZE = 24;

function useUrlState() {
  const [, setLocation] = useLocation();
  // Subscribe to the URL through wouter's useSearch so re-renders happen
  // through React's normal data flow instead of a manual popstate tick. The
  // previous tick/force pattern read window.location.search directly during
  // render, which decoupled the value from React's render cycle and made it
  // possible to render fewer hooks on a subsequent render (production
  // error #300) when paired with the early-return error branch below.
  const search = useSearch();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const brands = (params.get("brands") || "").split(",").filter(Boolean);
  const grades = (params.get("grades") || "").split(",").filter(Boolean);
  const types = (params.get("types") || "").split(",").filter(Boolean);
  const sort = params.get("sort") || "price_low";

  const update = useCallback(
    (next: { brands?: string[]; grades?: string[]; types?: string[]; sort?: string }) => {
      const p = new URLSearchParams(window.location.search);
      const setOrDelete = (k: string, v: string[] | undefined) => {
        if (!v || v.length === 0) p.delete(k);
        else p.set(k, v.join(","));
      };
      if (next.brands !== undefined) setOrDelete("brands", next.brands);
      if (next.grades !== undefined) setOrDelete("grades", next.grades);
      if (next.types !== undefined) setOrDelete("types", next.types);
      if (next.sort !== undefined) {
        if (next.sort === "price_low") p.delete("sort");
        else p.set("sort", next.sort);
      }
      const qs = p.toString();
      const path = window.location.pathname;
      const fullUrl = qs ? `${path}?${qs}` : path;
      // Wouter's setLocation pushes a new history entry by default, so the
      // back button restores the previous filter set.
      setLocation(fullUrl);
    },
    [setLocation],
  );

  return { brands, grades, types, sort, update, search };
}

function FilterPanel({
  data,
  selectedBrands,
  selectedGrades,
  onBrandToggle,
  onGradeToggle,
  onClearAll,
}: {
  data: CategoryResponse;
  selectedBrands: string[];
  selectedGrades: string[];
  onBrandToggle: (b: string) => void;
  onGradeToggle: (g: string) => void;
  onClearAll: () => void;
}) {
  return (
    <div className="space-y-5" data-testid="filter-panel">
      {data.facets.brands.length > 1 && (
        <div>
          <h2 className="text-sm font-semibold mb-2 text-foreground">Brand</h2>
          <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
            {data.facets.brands.map((b) => {
              const id = `brand-${b.name}`;
              const checked = selectedBrands.includes(b.name);
              return (
                <div key={b.name} className="flex items-center gap-2">
                  <Checkbox
                    id={id}
                    checked={checked}
                    onCheckedChange={() => onBrandToggle(b.name)}
                    data-testid={`filter-brand-${b.name}`}
                  />
                  <Label htmlFor={id} className="flex-1 text-sm flex items-baseline justify-between gap-2 cursor-pointer">
                    <span className="truncate">{b.name}</span>
                    <span className="text-xs text-muted-foreground tabular-nums">({b.count})</span>
                  </Label>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.facets.grades.length > 1 && (
        <div>
          <h2 className="text-sm font-semibold mb-1 text-foreground">Condition</h2>
          <p className="text-[11px] text-muted-foreground mb-2 flex items-center gap-1" data-testid="text-scale-hint">
            <Info className="h-3 w-3" />
            {data.category.gradeScaleHint}
          </p>
          <div className="space-y-1.5">
            {data.facets.grades.map((g) => {
              const id = `grade-${g.code}`;
              const checked = selectedGrades.includes(g.code);
              return (
                <div key={g.code} className="flex items-center gap-2">
                  <Checkbox
                    id={id}
                    checked={checked}
                    onCheckedChange={() => onGradeToggle(g.code)}
                    data-testid={`filter-grade-${g.code}`}
                  />
                  <Label htmlFor={id} className="flex-1 text-sm flex items-center justify-between gap-2 cursor-pointer">
                    <GradeTierBadge code={g.code} tier={g.tier} compact />
                    <span className="text-xs text-muted-foreground tabular-nums">({g.count})</span>
                  </Label>
                </div>
              );
            })}
          </div>
          <Link
            href={`/grading#${data.category.slug}`}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"
            data-testid="link-grading-guide"
          >
            How we grade {data.category.name} <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
      )}

      {(selectedBrands.length > 0 || selectedGrades.length > 0) && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={onClearAll}
          data-testid="button-clear-filters"
        >
          <X className="h-3.5 w-3.5 mr-1.5" /> Clear all filters
        </Button>
      )}
    </div>
  );
}

export default function CategoryPage() {
  const params = useParams<{ slug: string }>();
  const slug = params.slug;
  const [, setLocation] = useLocation();
  const { brands, grades, types, sort, update, search } = useUrlState();
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  // Live (client-side) text filter applied on top of whatever the API has
  // returned. Matches against brand + model name + description so users
  // can type "stainless", a partial SKU, or a brand and see the grid
  // narrow as they type.
  const [searchQuery, setSearchQuery] = useState("");
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Build the API query string (server applies brand/grade/type filters + sort).
  const baseQuery = useMemo(() => {
    const p = new URLSearchParams();
    if (brands.length) p.set("brands", brands.join(","));
    if (grades.length) p.set("grades", grades.join(","));
    if (types.length) p.set("types", types.join(","));
    if (sort && sort !== "price_low") p.set("sort", sort);
    p.set("perPage", String(PAGE_SIZE));
    return p;
  }, [brands, grades, types, sort]);

  const query = useInfiniteQuery<CategoryResponse>({
    queryKey: [`/api/categories/${slug}/products`, baseQuery.toString()],
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
    queryFn: async ({ pageParam }) => {
      const p = new URLSearchParams(baseQuery);
      p.set("page", String(pageParam));
      const res = await fetch(`/api/categories/${slug}/products?${p.toString()}`, {
        credentials: "include",
      });
      if (res.status === 404) {
        // Tag the error so the effect below can react and route us back to
        // the showroom landing. Calling setLocation directly from inside
        // queryFn would dispatch a router state update from a fetch promise,
        // which can race with React's render phase.
        const err = new Error("Category not found") as Error & { status?: number };
        err.status = 404;
        throw err;
      }
      if (!res.ok) throw new Error("Failed to load category");
      return res.json();
    },
    retry: (failureCount, err: any) => {
      if (err?.status === 404) return false;
      return failureCount < 3;
    },
  });

  // Redirect on 404 from inside an effect so router state updates never
  // happen during a render or from a fetch promise.
  useEffect(() => {
    const err: any = query.error;
    if (err?.status === 404) {
      setLocation("/showroom");
    }
  }, [query.error, setLocation]);

  const firstPage = query.data?.pages[0];
  useEffect(() => {
    if (firstPage?.category?.name) {
      document.title = `${firstPage.category.name} | OpenBox by ShopJimmy`;
    }
  }, [firstPage?.category?.name]);

  // IntersectionObserver-based infinite scroll
  useEffect(() => {
    if (!query.hasNextPage || query.isFetchingNextPage) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          query.fetchNextPage();
        }
      },
      { rootMargin: "400px 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

  const onBrandToggle = (b: string) =>
    update({ brands: brands.includes(b) ? brands.filter((x) => x !== b) : [...brands, b] });
  const onGradeToggle = (g: string) =>
    update({ grades: grades.includes(g) ? grades.filter((x) => x !== g) : [...grades, g] });
  const onTypeToggle = (t: string) =>
    update({ types: types.includes(t) ? types.filter((x) => x !== t) : [...types, t] });
  const onClearAll = () => update({ brands: [], grades: [], types: [] });
  const activeFilterCount = brands.length + grades.length + types.length;

  const allProducts: CategoryProduct[] = useMemo(
    () => query.data?.pages.flatMap((p) => p.products) ?? [],
    [query.data],
  );
  // Apply the live search filter client-side on top of the loaded pages.
  // Note: only matches against products currently fetched — if the user
  // types something that's only on a not-yet-loaded page, infinite scroll
  // will eventually pull it in as they keep scrolling. The empty-state
  // branch below keeps the sentinel mounted so IntersectionObserver can
  // still fetch more pages while a search is active and showing 0 hits.
  const trimmedSearch = searchQuery.trim().toLowerCase();
  const visibleProducts = useMemo(() => {
    if (!trimmedSearch) return allProducts;
    return allProducts.filter((p) => {
      const haystack = `${p.brand} ${p.modelName} ${p.description ?? ""}`.toLowerCase();
      return haystack.includes(trimmedSearch);
    });
  }, [allProducts, trimmedSearch]);
  const totalCount = firstPage?.totalCount ?? 0;
  const data = firstPage; // alias for legacy reads below
  const isLoading = query.isLoading;
  // When neither facet has more than one option there's nothing to filter
  // by, so we drop the entire sidebar / mobile Filter button and switch
  // the body back to a single-column layout.
  const hasFilters = !!data && (data.facets.brands.length > 1 || data.facets.grades.length > 1);

  // Error branch must come AFTER every hook in this component, otherwise an
  // error render would call fewer hooks than the previous render and trip
  // React production error #300 ("Rendered fewer hooks than expected"). The
  // 404 case is already redirecting via the effect above, so this only
  // shows for non-404 errors.
  if (query.error && (query.error as any)?.status !== 404) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Category unavailable.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4" data-testid="breadcrumb">
          <Link href="/" className="hover:text-foreground inline-flex items-center gap-1">
            <HomeIcon className="h-3 w-3" /> Home
          </Link>
          <ChevronRight className="h-3 w-3" />
          <Link href="/showroom" className="hover:text-foreground">Shop</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground font-medium" data-testid="breadcrumb-current">
            {data?.category?.name ?? slug}
          </span>
        </nav>

        {/* Hero */}
        <header className="mb-6">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground" data-testid="text-category-title">
            {data?.category?.name ?? <Skeleton className="h-9 w-48 inline-block" />}
          </h1>
          {data?.category?.heroBlurb && (
            <p className="text-muted-foreground mt-2 max-w-3xl">{data.category.heroBlurb}</p>
          )}
        </header>

        {/* Live search box — filters the loaded products as the user
            types. Restored from the legacy flat showroom now that the
            global header search has been removed. */}
        <div className="relative mb-4 max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={`Search ${data?.category?.name ?? "products"}…`}
            className="pl-9 pr-9 h-10"
            aria-label="Filter products"
            data-testid="input-category-search"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center text-muted-foreground hover:text-foreground rounded"
              aria-label="Clear search"
              data-testid="button-clear-category-search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Type chips (only when category has types defined) */}
        {data?.category?.types && data.category.types.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-3 mb-4 -mx-1 px-1" data-testid="type-chips">
            {data.category.types.map((t) => {
              const active = types.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => onTypeToggle(t)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    active
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card text-foreground border-border hover:bg-muted"
                  }`}
                  data-testid={`chip-type-${t}`}
                >
                  {t}
                </button>
              );
            })}
          </div>
        )}

        {/* Sort + count bar — sticky on mobile so the "Filter" trigger stays
            reachable while users scroll through the product grid. */}
        <div className="flex items-center justify-between gap-3 mb-4 pb-3 border-b border-border sticky top-0 z-30 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 lg:static lg:bg-transparent lg:backdrop-blur-0 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0 py-2 lg:py-0">
          <div className="text-sm text-muted-foreground" data-testid="text-result-count">
            {isLoading ? (
              <Skeleton className="h-4 w-32" />
            ) : trimmedSearch ? (
              <>
                <span className="font-semibold text-foreground" data-testid="text-search-match-count">
                  {visibleProducts.length}
                </span>{" "}
                {visibleProducts.length === 1 ? "match" : "matches"} for "
                <span className="text-foreground">{searchQuery}</span>"
                <span className="ml-1 text-xs">(of {totalCount} loaded)</span>
              </>
            ) : (
              <>
                <span className="font-semibold text-foreground">{totalCount}</span>{" "}
                {totalCount === 1 ? "product" : "products"}
                {activeFilterCount > 0 && (
                  <span className="ml-2 text-xs">
                    ({activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"} applied)
                  </span>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {hasFilters && (
            <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="lg:hidden"
                  data-testid="button-open-filters"
                >
                  <Filter className="h-4 w-4 mr-1.5" />
                  Filter
                  {activeFilterCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold rounded-full bg-primary text-primary-foreground px-1">
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[88vw] sm:max-w-sm overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter products</SheetTitle>
                </SheetHeader>
                <div className="py-4">
                  {data && (
                    <FilterPanel
                      data={data}
                      selectedBrands={brands}
                      selectedGrades={grades}
                      onBrandToggle={onBrandToggle}
                      onGradeToggle={onGradeToggle}
                      onClearAll={onClearAll}
                    />
                  )}
                </div>
                <SheetFooter className="flex flex-row gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={onClearAll}
                    data-testid="button-mobile-clear-filters"
                  >
                    Clear
                  </Button>
                  <Button
                    type="button"
                    className="flex-1"
                    onClick={() => setMobileFiltersOpen(false)}
                    data-testid="button-mobile-apply-filters"
                  >
                    Apply
                  </Button>
                </SheetFooter>
              </SheetContent>
            </Sheet>
            )}

            <Select value={sort} onValueChange={(v) => update({ sort: v })}>
              <SelectTrigger className="w-[180px]" data-testid="select-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`sort-option-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Two-column body — collapses to a single column when there are no
            meaningful filters (e.g. mixed-brand pallet categories with a
            single grade). */}
        <div className={`grid grid-cols-1 ${hasFilters ? "lg:grid-cols-[260px_1fr]" : ""} gap-6`}>
          {hasFilters && (
          <aside className="hidden lg:block">
            <div className="sticky top-4">
              {data ? (
                <FilterPanel
                  data={data}
                  selectedBrands={brands}
                  selectedGrades={grades}
                  onBrandToggle={onBrandToggle}
                  onGradeToggle={onGradeToggle}
                  onClearAll={onClearAll}
                />
              ) : (
                <Skeleton className="h-96 w-full" />
              )}
            </div>
          </aside>
          )}

          <main>
            {isLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="aspect-[3/4] rounded-xl" />
                ))}
              </div>
            ) : visibleProducts.length === 0 ? (
              <>
                <Card className="p-12 text-center" data-testid="empty-products">
                  <Boxes className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                  <p className="text-foreground font-medium">
                    {trimmedSearch
                      ? `No matches for "${searchQuery}" in the ${allProducts.length} products loaded`
                      : "No products match your filters"}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {trimmedSearch
                      ? query.hasNextPage
                        ? "Scroll down to keep loading more — matches further in the catalog will appear automatically."
                        : "Try a shorter or different search term."
                      : "Try clearing filters or browse another category."}
                  </p>
                  {trimmedSearch ? (
                    <Button variant="outline" className="mt-4" onClick={() => setSearchQuery("")}>
                      Clear search
                    </Button>
                  ) : activeFilterCount > 0 ? (
                    <Button variant="outline" className="mt-4" onClick={onClearAll}>
                      Clear filters
                    </Button>
                  ) : null}
                </Card>
                {/* Keep the sentinel mounted while searching so the user
                    isn't trapped in a "no matches" dead-end — the infinite
                    query will keep loading more pages and the filter will
                    re-evaluate as they arrive. */}
                {trimmedSearch && query.hasNextPage && (
                  <div
                    ref={sentinelRef}
                    className="h-12 flex items-center justify-center mt-6 text-sm text-muted-foreground"
                    data-testid="infinite-sentinel-search"
                  >
                    {query.isFetchingNextPage
                      ? "Searching more products…"
                      : "Scroll to search more of the catalog"}
                  </div>
                )}
              </>
            ) : (
              <>
                <div
                  className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4"
                  data-testid="grid-products"
                >
                  {visibleProducts.map((p) => (
                    <ProductCard
                      key={p.modelId}
                      id={p.modelId}
                      brand={p.brand}
                      modelName={p.modelName}
                      description={p.description}
                      imageUrl={p.imageUrl ?? undefined}
                      startingPrice={p.startingPrice}
                      availableGrades={p.grades as Array<"A" | "B+" | "B-">}
                      gradePrices={p.gradePrices}
                      msrp={p.msrp ?? undefined}
                      color={p.color ?? undefined}
                      attributes={p.attributes}
                      isComingSoonOnly={!p.hasInStock && p.hasPresale}
                      earliestShipDate={p.earliestShipDate}
                      category={data?.category?.name}
                      minOrderQty={p.minOrderQty}
                      orderMultiple={p.orderMultiple}
                      remoteWarehouseCodes={p.remoteWarehouseCodes}
                      isPallet={p.isPallet}
                      palletConstituentBrands={p.palletConstituentBrands}
                      palletConstituentImages={p.palletConstituentImages}
                      palletConstituentModelCount={p.palletConstituentModelCount}
                      palletTotalUnits={p.palletTotalUnits}
                      palletConstituents={p.palletConstituents}
                      palletMsrpTotalCents={p.palletMsrpTotalCents}
                      palletGrade={p.palletGrade}
                      palletPerItemStartingPriceCents={p.palletPerItemStartingPriceCents}
                      useTierBadges
                      backHref={`/category/${data?.category?.slug ?? ''}${search ? `?${search}` : ''}`}
                      listContext={{
                        listId: `category-${data?.category?.slug}`,
                        listName: data?.category?.name,
                      }}
                    />
                  ))}
                </div>
                {query.hasNextPage ? (
                  <div
                    ref={sentinelRef}
                    className="h-12 flex items-center justify-center mt-6 text-sm text-muted-foreground"
                    data-testid="infinite-sentinel"
                  >
                    {query.isFetchingNextPage ? "Loading more…" : "Scroll for more"}
                  </div>
                ) : totalCount > PAGE_SIZE ? (
                  <p className="text-center text-xs text-muted-foreground mt-8 pb-6" data-testid="end-of-results">
                    — End of results —
                  </p>
                ) : null}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
